import { observable, action, computed } from 'mobx';
import axios from '../../../libs/axios_csrf';
import { snakeCase } from '@ridi/object-case-converter';
import uuid from 'node-uuid';
import {
  validateCardNumericalFields,
  manageBmcCardRelationsAttributes,
  clearCircularStructure,
  updateBmcCardRelationsAttributes
} from '../common/CardHelpers';
import { unauthorizedMessageOrProvided } from '../../common_components/alert_helpers';
import Card from '../../board_components/stores/Card';

export default class BmcCard {
  store;
  id;
  @observable label;
  @observable name;
  @observable description;
  @observable selected = false;
  @observable preselected = false;
  @observable attachments = [];
  @observable attachmentFilesQueue = [];
  @observable status = 0;
  @observable originalBmcName = '';

  constructor(
    store,
    {
      id,
      idd,
      bmcBlockId,
      label = '',
      name = '',
      description = '',
      need = '',
      targetClientNumber = 0,
      localization = '',
      purpose = '',
      crPurpose = '',
      purposes = [],
      customerSegments = [],
      keyPropositions = [],
      kind = '',
      price = 0,
      pricingType = '',
      types = [],
      utility = '',
      address = '',
      contactInformation = '',
      blockType = '',
      cardSelects = [],
      totalCost = 0,
      relation = '',
      resultingClientsNumber = 0,
      position = 0,
      bmcCardRelations = [],
      attachments = [],
      attachmentFilesQueue = [],
      creator = {},
      status = 0,
      originalBmcName = ''
    },
    persisted = true
  ) {
    this.store = store;
    this.persisted = persisted;

    if (!id) {
      this.id = uuid.v4();
      this.idd = this.id;
    } else {
      this.id = id;
      this.idd = idd;
    }

    this.bmcBlockId = bmcBlockId ? bmcBlockId : this.store.id;
    this.position = position === undefined ? this.store.cards.length : position;
    this.label = label;
    this.name = name;
    this.description = description;
    this.need = need;
    this.targetClientNumber = targetClientNumber;
    this.localization = localization;
    this.purpose = purpose;
    this.crPurpose = crPurpose;
    this.purposes = purposes;
    this.kind = kind;
    this.price = price;
    this.pricingType = pricingType;
    this.types = types;
    this.utility = utility;
    this.address = address;
    this.contactInformation = contactInformation;
    this.blockType = blockType;
    this.totalCost = totalCost;
    this.relation = relation;
    this.resultingClientsNumber = resultingClientsNumber;

    this.cardSelects = cardSelects;
    this.customerSegments = customerSegments;
    this.keyPropositions = keyPropositions;

    this.position = position;

    this.bmcCardRelations = bmcCardRelations;
    this.attachments = store.rootStore.attachmentsStore.attachmentsFromJsonArray(
      attachments,
      this
    );
    this.attachmentFilesQueue = attachmentFilesQueue;
    // this.attachments = attachments;
    this.creator = creator ? creator : this.store.rootStore.user;
    this.status = status;
    this.originalBmcName = originalBmcName;
    //console.log(this);
  }

  @action
  destroy() {
    // ------------------  AJAX CALL ---------------
    axios
      .delete(`/bmc_cards/${this.id}.json`)
      .then(
        action(response => {
          // console.log(response);
          this.store.rootStore.bmcUpdate -= 1;
          this.store.rootStore.generateAlert({
            type: 'success',
            headline: 'card_deleted_success_alert'
          });
        })
      )
      .catch(
        action(error => {
          // console.log(error);
          this.store.rootStore.generateAlert({
            type: 'danger',
            headline: unauthorizedMessageOrProvided(error, 'card_error_alert')
          });
        })
      );

    //------------------------------------------------

    this.store.cards.remove(this);
    // this.store.updateFilteredCards();
  }

  @action
  update() {
    validateCardNumericalFields(this);
    manageBmcCardRelationsAttributes(this);
    updateBmcCardRelationsAttributes(this, this.bmcCardRelations);
    // clearCircularStructure(this);

    // ------  AJAX CALL ----
    axios
      .patch(`/bmc_cards/${this.id}.json`, {
        bmc_card: this.serialize()
      })
      .then(
        action(response => {
          //console.log("response: ", response);
          this.store.rootStore.bmcUpdate += 1;
          this.bmcCardRelations = response.data.bmcCardRelations;
          this.store.rootStore.generateAlert({
            type: 'success',
            headline: 'card_updated_success_alert'
          });
        })
      )
      .catch(
        action(error => {
          // console.log(error);
          this.store.rootStore.generateAlert({
            type: 'danger',
            headline: unauthorizedMessageOrProvided(error, 'card_error_alert')
          });
        })
      );
    //------------------------------------------------
  }

  @action
  addAttachment(file) {
    this.attachmentFilesQueue.push(file);
    // Save attachments only if the attachable has already been persisted in backend.
    if (this.persisted) {
      this.persistAttachments();
    }
  }

  @action
  removeFileFromQueue(file) {
    this.attachmentFilesQueue.remove(file);
  }

  @action
  persistAttachments() {
    if (this.persisted) {
      while (this.attachmentFilesQueue.length > 0) {
        const file = this.attachmentFilesQueue.pop();
        this.store.rootStore.attachmentsStore.create(file, this);
      }
    } else {
      throw 'AttachmentOnInvalidObjectException';
    }
  }

  canEditAttachments() {
    return this.store.rootStore.authorizationStore.canEdit;
  }

  serialize() {
    return {
      id: this.id,
      bmc_block_id: this.store.id,
      position: this.position,
      creator: this.creator,
      price: this.price,
      label: this.label,
      name: this.name,
      target_client_number: this.targetClientNumber,
      purpose: this.purpose,
      bmc_card_relations_attributes: this.bmcCardRelations.map(relation => {
        if (relation) {
          return {
            id: relation.id,
            kind: relation.kind,
            card_id: relation.cardId,
            _destroy: relation._destroy
          };
        }
      }),
      original_bmc_name: this.originalBmcName,
      status: this.status,

      // customer_segments: this.customerSegments,
      // key_propositions: this.keyPropositions,
      // card_selects: this.cardSelects,

      description: this.description,
      types: this.types,
      cr_purpose: this.crPurpose,
      relation: this.relation,
      resulting_clients_number: this.resultingClientsNumber,
      utility: this.utility,
      address: this.address,
      contact_information: this.contactInformation,
      block_type: this.blockType,
      total_cost: this.totalCost,
      pricing_type: this.pricingType,
      need: this.need,
      localization: this.localization
    };
  }

  @action
  destroyAttachment(attachment) {
    // ------------------  AJAX CALL ---------------
    axios
      .delete(`/attachments/${attachment.id}.json`)
      .then(
        action(response => {
          //console.log(response);
        })
      )
      .catch(
        action(error => {
          // console.log(error);
          this.store.rootStore.generateAlert({
            type: 'danger',
            headline: unauthorizedMessageOrProvided(error, 'card_error_alert')
          });
        })
      );
    //------------------------------------------------

    this.attachments.remove(attachment);
  }

  @action
  setName(name) {
    this.name = name;
  }

  // toJSON() {
  //     const cloneCard = { ...this };
  //     delete cloneCard.store;
  //
  //     return cloneCard;
  // }
}

// Required by the Attachments store
BmcCard.backendModelName = 'BmcCard';
